import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

export const CLIENTS_LIST_PATH = '/admin/clients';
export const DELIVERY_LIST_PATH = '/admin/delivery';
export const DELIVERY_MODULAR_PATH = '/admin/delivery/modular';
export const SYNDICATION_DEALS_LIST_PATH = '/admin/syndication-deals';
export const SYNDICATES_LIST_PATH = '/admin/syndicates';

export function clientsDeliveryModeRedirectPath(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('mode') !== 'delivery') return null;
  params.delete('mode');
  const qs = params.toString();
  return qs ? `${DELIVERY_LIST_PATH}?${qs}` : DELIVERY_LIST_PATH;
}

export function isDeliveryListPath(path: string): boolean {
  return path === DELIVERY_LIST_PATH || path.startsWith(`${DELIVERY_LIST_PATH}/`);
}

export function isModularListPath(path: string): boolean {
  return path === DELIVERY_MODULAR_PATH || path.startsWith(`${DELIVERY_MODULAR_PATH}/`);
}

export function isSafeListReturnPath(path: string): boolean {
  return path === CLIENTS_LIST_PATH || isDeliveryListPath(path) || path === SYNDICATES_LIST_PATH;
}

export function investorsSyndicatesTabRedirectPath(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('tab') !== 'syndicates') return null;
  params.delete('tab');
  const qs = params.toString();
  return qs ? `${SYNDICATES_LIST_PATH}?${qs}` : SYNDICATES_LIST_PATH;
}

export function safeListReturnPath(path: unknown, fallbackPath = CLIENTS_LIST_PATH): string {
  if (typeof path !== 'string') return fallbackPath;
  if (isSafeListReturnPath(path)) return path;
  return fallbackPath;
}

export function listBackUrl(
  returnPath: unknown,
  returnParams: unknown,
  fallbackPath = CLIENTS_LIST_PATH
): string {
  const path = safeListReturnPath(returnPath, fallbackPath);
  if (typeof returnParams === 'string' && returnParams.length > 0) {
    return `${path}?${returnParams}`;
  }
  return path;
}

export const CLIENT_DETAIL_FROM_MODULAR = 'modular';
export const CLIENT_DETAIL_FROM_DELIVERY = 'delivery';
export const CLIENT_DETAIL_FROM_GROWTH = 'growth';

export type DeliveryListFrom = 'modular' | 'delivery' | 'growth';

export function isDeliveryListFrom(value: string | null | undefined): value is DeliveryListFrom {
  return (
    value === CLIENT_DETAIL_FROM_MODULAR ||
    value === CLIENT_DETAIL_FROM_DELIVERY ||
    value === CLIENT_DETAIL_FROM_GROWTH
  );
}

export function listFromForPage(
  lockedMode: 'delivery' | undefined,
  lockedView?: 'modular'
): DeliveryListFrom | undefined {
  if (lockedView === 'modular') return CLIENT_DETAIL_FROM_MODULAR;
  if (lockedMode === 'delivery') return CLIENT_DETAIL_FROM_DELIVERY;
  return undefined;
}

function appendListFrom(params: URLSearchParams, from?: string | null): void {
  if (isDeliveryListFrom(from)) params.set('from', from);
}

function pathForListFrom(fromQuery: string | null | undefined): string | undefined {
  if (fromQuery === CLIENT_DETAIL_FROM_MODULAR) return DELIVERY_MODULAR_PATH;
  if (fromQuery === CLIENT_DETAIL_FROM_DELIVERY) return DELIVERY_LIST_PATH;
  if (fromQuery === CLIENT_DETAIL_FROM_GROWTH) return DELIVERY_MODULAR_PATH;
  return undefined;
}

/** Old deal history stored only `returnParams: mode=delivery` (pre-route-split). */
export function remapLegacyDeliveryReturnParams(returnParams: unknown): string | null {
  if (typeof returnParams !== 'string' || returnParams.length === 0) return null;
  const params = new URLSearchParams(returnParams);
  if (params.get('mode') !== 'delivery') return null;
  params.delete('mode');
  const qs = params.toString();
  return qs ? `${DELIVERY_LIST_PATH}?${qs}` : DELIVERY_LIST_PATH;
}

export function resolveClientDetailReturnPath(
  stateReturnPath: unknown,
  fromQuery: string | null | undefined
): string {
  if (typeof stateReturnPath === 'string' && isSafeListReturnPath(stateReturnPath)) {
    return stateReturnPath;
  }
  return pathForListFrom(fromQuery) || CLIENTS_LIST_PATH;
}

export function hasExplicitListReturn(
  stateReturnPath: unknown,
  fromQuery: string | null | undefined
): boolean {
  if (typeof stateReturnPath === 'string' && isSafeListReturnPath(stateReturnPath)) {
    return true;
  }
  return isDeliveryListFrom(fromQuery);
}

/** Pipeline session wins only when this hop did not name a list. */
export function shouldUsePipelineBack(explicit: boolean, sessionFlag: boolean): boolean {
  return sessionFlag && !explicit;
}

export function clientDetailHref(
  clientId: string,
  options?: { tab?: string; from?: string | null; opportunityId?: string | null }
): string {
  const params = new URLSearchParams();
  if (options?.tab) params.set('tab', options.tab);
  if (options?.opportunityId) params.set('opportunityId', options.opportunityId);
  appendListFrom(params, options?.from);
  const qs = params.toString();
  return qs ? `/admin/clients/${clientId}?${qs}` : `/admin/clients/${clientId}`;
}

export function clientCommercialsHref(clientId: string, fromModular = false): string {
  return clientDetailHref(clientId, {
    tab: 'commercials',
    from: fromModular ? CLIENT_DETAIL_FROM_MODULAR : undefined,
  });
}

export function syndicationDealsListHref(options?: { from?: string | null }): string {
  const params = new URLSearchParams();
  appendListFrom(params, options?.from);
  const qs = params.toString();
  return qs ? `${SYNDICATION_DEALS_LIST_PATH}?${qs}` : SYNDICATION_DEALS_LIST_PATH;
}

export function syndicationDealHref(
  dealId: string,
  options?: { tab?: string; from?: string | null; viaList?: boolean }
): string {
  const params = new URLSearchParams();
  if (options?.viaList) params.set('deal', dealId);
  if (options?.tab) params.set('tab', options.tab);
  appendListFrom(params, options?.from);
  const qs = params.toString();
  if (options?.viaList) {
    return qs ? `${SYNDICATION_DEALS_LIST_PATH}?${qs}` : SYNDICATION_DEALS_LIST_PATH;
  }
  return qs
    ? `${SYNDICATION_DEALS_LIST_PATH}/${dealId}?${qs}`
    : `${SYNDICATION_DEALS_LIST_PATH}/${dealId}`;
}

export function clientDetailBackLabel(
  backFromPipeline: boolean,
  returnPath: string,
  fromQuery?: string | null
): string {
  if (backFromPipeline) return 'Back to pipeline';
  if (fromQuery === CLIENT_DETAIL_FROM_GROWTH) return 'Back to Growth';
  if (isModularListPath(returnPath)) return 'Back to Capital Raise';
  if (isDeliveryListPath(returnPath)) return 'Back to Delivery';
  return 'Back to Client Management';
}

export function clientsListReturnState(
  lockedMode: 'delivery' | undefined,
  searchParams: URLSearchParams,
  lockedView?: 'modular'
): { returnPath: string; returnParams: string } {
  const params = new URLSearchParams(searchParams);
  if (lockedMode === 'delivery') {
    params.delete('mode');
  }
  const returnPath =
    lockedMode === 'delivery'
      ? lockedView === 'modular'
        ? DELIVERY_MODULAR_PATH
        : DELIVERY_LIST_PATH
      : CLIENTS_LIST_PATH;
  return {
    returnPath,
    returnParams: params.toString(),
  };
}

export function syndicationLocationState(
  state: unknown
): { returnParams?: unknown; returnPath?: unknown } | null {
  return typeof state === 'object' && state !== null
    ? (state as { returnParams?: unknown; returnPath?: unknown })
    : null;
}

export function syndicationDealBackNav(
  state: { returnParams?: unknown; returnPath?: unknown } | null,
  fromQuery?: string | null
): { to: string; label: string } {
  const returnParams = state?.returnParams;
  const returnPath =
    typeof state?.returnPath === 'string' && isSafeListReturnPath(state.returnPath)
      ? state.returnPath
      : pathForListFrom(fromQuery);
  const legacyDelivery = !returnPath ? remapLegacyDeliveryReturnParams(returnParams) : null;
  if (legacyDelivery) {
    return { to: legacyDelivery, label: '← Delivery' };
  }
  const fromClients = returnPath !== undefined || typeof returnParams === 'string';
  if (!fromClients) {
    return { to: SYNDICATION_DEALS_LIST_PATH, label: '← Syndication Deals' };
  }
  const path = returnPath || CLIENTS_LIST_PATH;
  const to =
    typeof returnParams === 'string' && returnParams ? `${path}?${returnParams}` : path;
  const wantsGrowth =
    fromQuery === CLIENT_DETAIL_FROM_GROWTH ||
    (typeof returnParams === 'string' && returnParams.includes('board=growth'));
  if (wantsGrowth && isModularListPath(path)) {
    return { to, label: 'Back to Growth' };
  }
  const label = isModularListPath(path)
    ? 'Back to Capital Raise'
    : isDeliveryListPath(path)
      ? '← Delivery'
      : path === SYNDICATES_LIST_PATH
        ? '← Syndicates'
        : '← Clients';
  return { to, label };
}

export function resolveSyndicationDealReturn(
  state: { returnParams?: unknown; returnPath?: unknown } | null,
  fromQuery?: string | null
): { to: string; label: string } {
  return syndicationDealBackNav(state, fromQuery);
}

export function RedirectClientsDeliveryMode({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const to = clientsDeliveryModeRedirectPath(searchParams.toString());
  if (to) return <Navigate to={to} replace />;
  return <>{children}</>;
}

export function RedirectInvestorsSyndicatesTab({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const to = investorsSyndicatesTabRedirectPath(searchParams.toString());
  if (to) return <Navigate to={to} replace />;
  return <>{children}</>;
}
